import { ReferencedEntry } from 'src/@types/contentful';
import { ContentType, Navigation, NavigationType } from 'src/api/contentful/contentTypes.cloverdotcom';
import { ContentfulFilters, fetchEntries } from 'src/api/contentful/fetchEntries';
import { get } from 'src/utils/EntryUtil';
import { FlatEntry, flattenContentfulData } from '../flattenContentful';
import { CmsState, Status } from '../typesUtil';

/**
 * Function that gets flattened main menu data from CMS
 * @param language current i18n language, accepts locale code (eg. en-US)
 * @returns Promise resolving to CMS state type with flattened main menu data
 */
 export async function getFlattenedMainMenu(language: string): Promise<CmsState<FlatEntry<Navigation>>> {
  const data = await getFlattenedNavigation(language, {
    contentType: ContentType.NAVIGATION,
      include: 4,
      filters: { fields: { type: NavigationType.HEADER_V2 } },
  });
  return data;
}

/**
 * Function that gets flattened util menu data from CMS
 * @param language current i18n language, accepts locale code (eg. en-US)
 * @returns Promise resolving to CMS state type with flattened util menu data
 */
export async function getFlattenedUtilMenu(language: string): Promise<CmsState<FlatEntry<Navigation>>> {
  const data = await getFlattenedNavigation(language, {
    contentType: ContentType.NAVIGATION,
      include: 3,
      filters: { fields: { type: NavigationType.HEADER_UTIL_V2 } },
  });
  return data;
}

/**
 * Function that queries the CMS, and passes the response through decoupling process
 * @param language current i18n language, accepts locale code (eg. en-US)
 * @param filters ContentfulFilters
 * @returns Promise that resolves to an object of type CMS State containing the flattened data
 */
export async function getFlattenedNavigation(language: string, filters: ContentfulFilters): Promise<CmsState<FlatEntry<Navigation>>> {
  let resp;

  try {
    resp = await fetchEntries<Navigation>(language, filters);
  }
  catch (err) {
    return {
      status: Status.ERRORED,
      message: `Contentful Response for Navigation not available: ${err.message}`,
    };
  }

  const data = get(resp, 'items', [])[0] as ReferencedEntry<Navigation>;
  const flattenedData = flattenContentfulData(data);

  if (!flattenedData) {
    return {
      status: Status.ERRORED,
      message: 'Unable to flatten Contentful Response for Navigation.',
    };
  }

  return {
    data: flattenedData,
    status: Status.RESOLVED,
  };
}
